
import type { VisualPayload } from "../../types";
import CardShell from "./_CardShell";

const TYPE_COLORS: Record<string, string> = {
  Normal: "bg-gray-200 text-gray-800 border-gray-300",
  Fire: "bg-orange-200 text-orange-900 border-orange-400",
  Water: "bg-blue-200 text-blue-900 border-blue-400",
  Electric: "bg-yellow-200 text-yellow-900 border-yellow-400",
  Grass: "bg-green-200 text-green-900 border-green-400",
  Ice: "bg-cyan-100 text-cyan-900 border-cyan-300",
  Fighting: "bg-red-300 text-red-900 border-red-400",
  Poison: "bg-purple-200 text-purple-900 border-purple-400",
  Ground: "bg-yellow-300 text-yellow-900 border-yellow-500",
  Flying: "bg-indigo-100 text-indigo-900 border-indigo-300",
  Psychic: "bg-pink-200 text-pink-900 border-pink-400",
  Bug: "bg-lime-200 text-lime-900 border-lime-400",
  Rock: "bg-yellow-400 text-yellow-900 border-yellow-600",
  Ghost: "bg-violet-200 text-violet-900 border-violet-400",
  Dragon: "bg-indigo-300 text-indigo-900 border-indigo-500",
  Dark: "bg-gray-700 text-gray-100 border-gray-800",
  Steel: "bg-slate-200 text-slate-900 border-slate-400",
  Fairy: "bg-pink-100 text-pink-900 border-pink-300",
};

const BUCKET_LABELS: Record<string, string> = {
  double_weak: "Double Weak (x2.56+)",
  weak: "Weak (x1.25–2.56)",
  resist: "Resist (x0.4–0.8)",
  double_resist: "Double Resist (≤0.4)",
  neutral: "Neutral (x0.8–1.25)",
};

function TypeBadge({ type }: { type: string }) {
  return (
    <span
      className={`inline-block px-2 py-1 rounded border text-xs font-semibold mr-1 mb-1 ${
        TYPE_COLORS[type] || "bg-gray-100 text-gray-800 border-gray-200"
      }`}
    >
      {type}
    </span>
  );
}

interface TypeEffectivenessSection {
  section: "type_effectiveness";
  double_weak: string[];
  weak: string[];
  resist: string[];
  double_resist: string[];
  neutral: string[];
}

interface RaidCounterEntry {
  name: string;
  type1: string;
  type2?: string | null;
  base_attack: number;
  best_fast_move?: string | null;
  best_charge_move_1?: string | null;
  best_charge_move_2?: string | null;
  /** The PvPoke league ranking the moves come from ("master", else "ultra" or "great"). */
  moves_league?: string | null;
  /** Why there are no moves, when PvPoke ranks the counter in no league. */
  moves_note?: string | null;
  edps?: number | null;
  role?: string | null;
}

interface RaidCountersSection {
  section: "raid_counters";
  counters: RaidCounterEntry[];
}

function RaidCounterRow({ counter }: { counter: RaidCounterEntry }) {
  const types = [counter.type1, counter.type2].filter(Boolean) as string[];
  const moves = [counter.best_fast_move, counter.best_charge_move_1, counter.best_charge_move_2].filter(Boolean);
  return (
    // Two-row layout — name + types on top, moves wrap onto their own line.
    // Prevents horizontal overflow on mobile when move names are long
    // (e.g. "Dragon Tail · Outrage · Breaking Swipe").
    <div className="py-1.5 border-b border-gray-100 last:border-0 text-sm">
      <div className="flex items-center gap-2 min-w-0">
        {typeof counter.role === "string" && (
          <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-white/10 text-white/70 shrink-0">{counter.role}</span>
        )}
        <span className="font-medium flex-1 min-w-0 truncate">{counter.name}</span>
        {typeof counter.edps === "number" && (
          <span className="text-xs font-mono text-white/60 shrink-0" title="effective DPS">{counter.edps.toFixed(1)}</span>
        )}
        <div className="flex gap-1 shrink-0">
          {types.map((t) => (
            <TypeBadge key={t} type={t} />
          ))}
        </div>
      </div>
      {moves.length > 0 ? (
        <div className="mt-0.5 text-gray-500 text-xs leading-relaxed break-words">
          {moves.join(" · ")}
          {counter.moves_league && counter.moves_league !== "master" && (
            <span className="text-gray-400"> ({counter.moves_league} league moveset)</span>
          )}
        </div>
      ) : (
        <div className="mt-0.5 text-gray-400 text-xs italic">
          {counter.moves_note ?? "No recommended moveset: PvPoke doesn't rank this Pokémon in any league"}
        </div>
      )}
    </div>
  );
}


/** A bare type's attacking lists and, for two types, the multiplier each way (plan phase 2). */
function AttackingBlocks({ payload }: { payload: VisualPayload }) {
  const atk = payload.sections.find((s) => s.section === "attacking") as Record<string, unknown> | undefined;
  const pair = payload.sections.find((s) => s.section === "pair") as Record<string, unknown> | undefined;
  if (!atk && !pair) return null;
  const list = (k: string) => (atk && Array.isArray(atk[k]) ? (atk[k] as string[]) : []);
  return (
    <div className="mt-4 space-y-3">
      {pair && (
        <p className="text-sm text-white/85">
          {String(pair.attacker)} → {String(pair.defender)}: ×{Number(pair.multiplier)} · {String(pair.defender)} → {String(pair.attacker)}: ×{Number(pair.reverse)}
        </p>
      )}
      {atk && (
        <div>
          <p className="text-[10px] text-white/30 uppercase tracking-wide mb-1">{String(atk.attacker)} attacks</p>
          <p className="text-[11px] text-white/40 mb-1">Super effective against</p>
          <div>{list("super_effective").map((t) => <TypeBadge key={t} type={t} />)}</div>
          <p className="text-[11px] text-white/40 mb-1 mt-2">Weak against</p>
          <div>{[...list("not_very_effective"), ...list("immune")].map((t) => <TypeBadge key={t} type={t} />)}</div>
        </div>
      )}
    </div>
  );
}

export default function TypeCard({ payload }: { payload: VisualPayload }) {
  const section = payload.sections.find((s) => s.section === "type_effectiveness") as TypeEffectivenessSection | undefined;
  const raidSection = payload.sections.find((s) => s.section === "raid_counters") as RaidCountersSection | undefined;

  if (!section) {
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
        <p className="text-sm text-gray-400">No type effectiveness data available.</p>
      </CardShell>
    );
  }

  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
      <div className="space-y-2">
        {Object.entries(BUCKET_LABELS).map(([bucket, label]) => {
          const types = (section as any)[bucket] as string[];
          if (!types || types.length === 0) return null;
          return (
            <div key={bucket}>
              <div className="font-semibold text-sm mb-1">{label}</div>
              <div className="flex flex-wrap gap-1 mb-2">
                {types.map((type) => (
                  <TypeBadge key={type} type={type} />
                ))}
              </div>
            </div>
          );
        })}
        {raidSection && raidSection.counters.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="font-semibold text-sm mb-2">Top Raid Counters</div>
            <div>
              {raidSection.counters.map((c) => (
                <RaidCounterRow key={c.name} counter={c} />
              ))}
            </div>
          </div>
        )}
      </div>
          <AttackingBlocks payload={payload} />
    </CardShell>
  );
}
